import { createContext, useContext, useEffect, useRef } from 'react';
import type { ButtonHTMLAttributes, ComponentProps, FocusEvent, KeyboardEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface NavMenuContextValue {
  close: () => void;
}
const NavMenuContext = createContext<NavMenuContextValue>({ close: () => undefined });

interface NavMenuProps {
  id: string;
  /** Lifted state: only one menu is open at a time. */
  openId: string | null;
  setOpenId: (id: string | null) => void;
  trigger: ReactNode;
  triggerClassName: string;
  triggerLabel: string;
  align?: 'left' | 'right';
  children: ReactNode;
}

const ITEM_SELECTOR = '[role="menuitem"]';

/**
 * NavMenu - small dropdown used by the Manage, Tools and Profile menus (CRM-178).
 * Pattern from components/lists/ListSortMenu.tsx, plus Escape, arrow keys and focus handling.
 * Not a dialog: there is no focus trap. The panel renders immediately after the trigger so Tab
 * from the trigger enters the first item.
 */
export function NavMenu({
  id,
  openId,
  setOpenId,
  trigger,
  triggerClassName,
  triggerLabel,
  align = 'left',
  children,
}: NavMenuProps) {
  const open = openId === id;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const focusFirstOnOpen = useRef(false);

  const focusItem = (which: 'first' | 'last' | 'next' | 'prev') => {
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    let index = 0;
    if (which === 'last') index = items.length - 1;
    else if (which === 'next') index = current < 0 ? 0 : (current + 1) % items.length;
    else if (which === 'prev') index = current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length;
    items[index].focus();
  };

  // Keyboard open focuses the first item; mouse open leaves focus on the trigger.
  useEffect(() => {
    if (open && focusFirstOnOpen.current) {
      focusItem('first');
    }
    focusFirstOnOpen.current = false;
  }, [open]);

  // Outside clicks close via mousedown only (focusout never treats a null relatedTarget as "outside").
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpenId(null);
    };
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenId(null);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpenId]);

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    // Only a real move of focus to an element outside this menu closes it.
    if (open && next && wrapperRef.current && !wrapperRef.current.contains(next)) setOpenId(null);
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // The native click that follows toggles the menu; remember it was the keyboard.
      focusFirstOnOpen.current = true;
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (open) {
        focusItem('first');
      } else {
        focusFirstOnOpen.current = true;
        setOpenId(id);
      }
    }
  };

  const handlePanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusItem('next');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusItem('prev');
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusItem('first');
    } else if (e.key === 'End') {
      e.preventDefault();
      focusItem('last');
    }
  };

  return (
    <div className="relative" ref={wrapperRef} onBlur={handleBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerLabel}
        onMouseDown={() => {
          focusFirstOnOpen.current = false;
        }}
        onKeyDown={handleTriggerKeyDown}
        onClick={() => setOpenId(open ? null : id)}
      >
        {trigger}
      </button>
      {open && (
        <div
          ref={panelRef}
          role="menu"
          onKeyDown={handlePanelKeyDown}
          className={`absolute z-50 mt-2 min-w-[12rem] py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <NavMenuContext.Provider value={{ close: () => setOpenId(null) }}>{children}</NavMenuContext.Provider>
        </div>
      )}
    </div>
  );
}

const ITEM_CLASSES =
  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium transition-colors text-gray-700 dark:text-gray-300 hover:text-blast-600 dark:hover:text-blast-400 hover:bg-gray-50 dark:hover:bg-gray-800';

/** Menu item that is a router link; picking it closes the menu. */
export function NavMenuLink({ onClick, className, ...rest }: ComponentProps<typeof Link>) {
  const { close } = useContext(NavMenuContext);
  return (
    <Link
      {...rest}
      role="menuitem"
      className={className ?? ITEM_CLASSES}
      onClick={(e) => {
        close();
        onClick?.(e);
      }}
    />
  );
}

interface NavMenuButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Keep the menu open after the click (e.g. the theme cycler). */
  keepOpen?: boolean;
}

/** Menu item that is a button; closes the menu unless keepOpen. */
export function NavMenuButton({ keepOpen, onClick, className, ...rest }: NavMenuButtonProps) {
  const { close } = useContext(NavMenuContext);
  return (
    <button
      {...rest}
      type="button"
      role="menuitem"
      className={className ?? ITEM_CLASSES}
      onClick={(e) => {
        if (!keepOpen) close();
        onClick?.(e);
      }}
    />
  );
}
